import { jsPDF } from 'jspdf';
import JsBarcode from 'jsbarcode';

const BARCODES_PER_ROW = 2;
const BARCODES_PER_PAGE = 10;
const PAGE_WIDTH = 210; // A4 width in mm
const PAGE_HEIGHT = 297; // A4 height in mm
const MARGIN = 15;
const HEADER_HEIGHT = 20;

interface BarcodeData {
  value: string;
}

function generateBarcodeCanvas(value: string): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  try {
    JsBarcode(canvas, value, {
      format: 'CODE128',
      width: 2,
      height: 50,
      displayValue: true,
      background: '#ffffff',
      lineColor: '#000000',
      margin: 5,
      fontSize: 12,
      font: 'monospace',
    });
  } catch {
    // If barcode generation fails, create a placeholder
    const ctx = canvas.getContext('2d');
    if (ctx) {
      canvas.width = 200;
      canvas.height = 80;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#000000';
      ctx.font = '12px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(`Invalid: ${value}`, canvas.width / 2, canvas.height / 2);
    }
  }
  return canvas;
}

export async function generateBarcodePdf(
  barcodes: BarcodeData[],
  bargeName: string
): Promise<void> {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const contentWidth = PAGE_WIDTH - MARGIN * 2;
  const cellWidth = contentWidth / BARCODES_PER_ROW;
  const cellHeight = (PAGE_HEIGHT - MARGIN * 2 - HEADER_HEIGHT) / (BARCODES_PER_PAGE / BARCODES_PER_ROW);

  let currentPage = 1;
  const totalPages = Math.ceil(barcodes.length / BARCODES_PER_PAGE);

  for (let i = 0; i < barcodes.length; i++) {
    const indexOnPage = i % BARCODES_PER_PAGE;

    // Add new page if needed (except for first barcode)
    if (indexOnPage === 0 && i > 0) {
      pdf.addPage();
      currentPage++;
    }

    // Draw header on first barcode of each page
    if (indexOnPage === 0) {
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${bargeName} - Barcodes`, MARGIN, MARGIN + 5);

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(
        `Page ${currentPage} of ${totalPages} | Total: ${barcodes.length} barcodes`,
        PAGE_WIDTH - MARGIN,
        MARGIN + 5,
        { align: 'right' }
      );

      // Draw line under header
      pdf.setDrawColor(200, 200, 200);
      pdf.line(MARGIN, MARGIN + 10, PAGE_WIDTH - MARGIN, MARGIN + 10);
    }

    // Calculate position
    const row = Math.floor(indexOnPage / BARCODES_PER_ROW);
    const col = indexOnPage % BARCODES_PER_ROW;
    const x = MARGIN + col * cellWidth;
    const y = MARGIN + HEADER_HEIGHT + row * cellHeight;

    // Generate barcode canvas
    const canvas = generateBarcodeCanvas(barcodes[i].value);

    // Convert canvas to image data
    const imgData = canvas.toDataURL('image/png');

    // Calculate image dimensions to fit in cell
    const imgWidth = cellWidth - 10;
    const imgHeight = (canvas.height / canvas.width) * imgWidth;

    // Center the barcode in the cell
    const imgX = x + (cellWidth - imgWidth) / 2;
    const imgY = y + (cellHeight - imgHeight) / 2 - 5;

    // Add barcode image
    pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth, imgHeight);

    // Draw cell border (light gray)
    pdf.setDrawColor(230, 230, 230);
    pdf.rect(x + 2, y + 2, cellWidth - 4, cellHeight - 4);
  }

  // Add footer to last page
  pdf.setFontSize(8);
  pdf.setTextColor(128, 128, 128);
  pdf.text(
    `Generated on ${new Date().toLocaleString()}`,
    PAGE_WIDTH / 2,
    PAGE_HEIGHT - 10,
    { align: 'center' }
  );

  // Save the PDF
  pdf.save(`${bargeName}-barcodes.pdf`);
}
